<script setup>
import { nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { useAlert as wgptUseAlert } from 'dashboard/composables/wgpt';
import { useAlert } from 'dashboard/composables';
import { useRoute } from 'vue-router';

import SnackbarContainer from 'next/wgpt/Snackbar/SnackbarContainer.vue';
import Dialog from 'next/dialog/Dialog.vue';
import CreateBotSourceForm from './CreateBotSourceForm.vue';

const emit = defineEmits(['close']);
const store = useStore();
const getters = useStoreGetters();
const route = useRoute();
const { t } = useI18n();

const dialogRef = ref(null);
const isOpen = ref(false);
const uiFlags = getters['wgptBotSources/getUIFlags'];

const onClose = () => {
  isOpen.value = false;
  emit('close');
};

const formRef = ref(null);

const open = () => {
  isOpen.value = true;
  nextTick(() => {
    if (!dialogRef.value) return;
    dialogRef.value.open();
  });
};

const close = () => {
  dialogRef.value.close();
  onClose();
};

const handleSubmit = async () => {
  try {
    if (!formRef.value) return;
    const { validate } = formRef.value;

    const data = await validate();
    if (!data) return;

    const botSource = {
      source_type: 'custom',
      ...data,
    };
    await store.dispatch('wgptBotSources/create', {
      ...botSource,
      botId: route.params.botId,
    });

    useAlert(
      t(
        'WGPT_BOTS.EDIT.TABS.KNOWLEDGE_BASE.TABS.ITEMS.CREATE.API.SUCCESS_MESSAGE'
      )
    );
    close();
  } catch (error) {
    const errorMessage =
      error?.response?.message ||
      t(
        'WGPT_BOTS.EDIT.TABS.KNOWLEDGE_BASE.TABS.ITEMS.CREATE.API.ERROR_MESSAGE'
      );
    wgptUseAlert(errorMessage);
  }
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('WGPT_BOTS.EDIT.TABS.KNOWLEDGE_BASE.TABS.ITEMS.CREATE.TITLE')"
    :confirm-button-label="
      t('WGPT_BOTS.EDIT.TABS.KNOWLEDGE_BASE.TABS.ITEMS.CREATE.SAVE')
    "
    :cancel-button-label="
      t('WGPT_BOTS.EDIT.TABS.KNOWLEDGE_BASE.TABS.ITEMS.CREATE.CANCEL')
    "
    :is-loading="uiFlags.isCreating"
    :disable-confirm-button="uiFlags.isCreating"
    overflow-y-auto
    @confirm="handleSubmit"
    @close="onClose"
  >
    <SnackbarContainer class="-top-6 fixed -translate-y-full" />
    <CreateBotSourceForm v-if="isOpen" ref="formRef" @submit="handleSubmit" />
  </Dialog>
</template>
